import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  ShieldCheck, Crown, Zap, TrendingUp, Users, Award,
  CheckCircle2, Sparkles, ArrowRight, Loader2, Flame, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Country-aware pricing — mirrors backend POWER_PRICES
const POWER_QUOTES = {
  IN: { amount: 9999, currency: 'INR', symbol: '₹', label: '₹9,999', referralPayout: '₹750', breakeven: 14, recoupGoal: 12 },
  UK: { amount: 99,   currency: 'GBP', symbol: '£', label: '£99',    referralPayout: '£10',  breakeven: 11, recoupGoal: 10 },
  CA: { amount: 149,  currency: 'CAD', symbol: 'C$', label: 'C$149', referralPayout: 'C$15', breakeven: 11, recoupGoal: 10 },
};

const PowerPartnerUpgrade = ({ user }) => {
  const [country, setCountry] = useState((user?.country || 'IN').toUpperCase());
  const [status, setStatus] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const quote = POWER_QUOTES[country] || POWER_QUOTES.IN;
  const userCity = user?.city || '';

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/partner/status`);
      setStatus(res.data);
    } catch (e) {
      logger.error('Partner status fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    try {
      const params = { country };
      if (userCity) params.city = userCity;
      const res = await axios.get(`${API_URL}/api/partner/power/availability`, { params });
      setAvailability(res.data);
    } catch (e) {
      logger.error('Availability fetch failed:', e);
    }
  }, [country, userCity]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/partner/power/subscribe`, {
        origin_url: window.location.origin,
        country,
      });
      if (res.data?.already_active) {
        toast.success('You\'re already a Power Partner.');
        await fetchStatus();
        return;
      }
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout. Please retry.');
      }
    } catch (e) {
      logger.error('Power checkout failed:', e);
      toast.error(e?.response?.data?.detail || 'Checkout failed. Please retry.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isPower = status?.tier === 'power' && status?.status === 'active';
  const isStandard = status?.status === 'active' && status?.tier !== 'power';

  // ────────── ALREADY POWER ──────────
  if (isPower) {
    return (
      <div data-testid="power-partner-active" className="space-y-6">
        <div
          className="relative overflow-hidden rounded-2xl p-8 text-white"
          style={{ background: 'linear-gradient(135deg, #0A0A0C 0%, #1F2937 50%, #0A0A0C 100%)' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20"
               style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <Crown className="h-8 w-8" style={{ color: '#D4AF37' }} />
              <span className="text-xs tracking-[0.3em] uppercase font-bold" style={{ color: '#D4AF37' }}>
                Verified Power Partner — Active
              </span>
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-2">
              You're already in the elite tier.
            </h2>
            <p className="text-white/70 text-base mb-6">
              Bulk Upload, priority NRI buyer routing and AI-matched warm leads are unlocked until {' '}
              <span style={{ color: '#D4AF37' }} className="font-semibold">
                {status?.expires_at ? new Date(status.expires_at).toLocaleDateString() : 'further notice'}
              </span>.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              {[
                ['Bulk uploads', 'Unlimited'],
                ['Priority routing', 'Active'],
                ['Power Agent badge', 'Visible'],
                ['Affiliate', `${quote.referralPayout}/ref`],
              ].map(([k, v]) => (
                <div key={k} className="border-l-2 pl-3" style={{ borderColor: '#D4AF37' }}>
                  <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold">{k}</div>
                  <div className="text-base font-semibold">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ────────── UPGRADE PITCH ──────────
  return (
    <div data-testid="power-partner-upgrade" className="space-y-8">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-8 sm:p-12 text-white"
        style={{ background: 'linear-gradient(135deg, #0A0A0C 0%, #1F2937 50%, #0A0A0C 100%)' }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-15"
             style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }} />
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 border"
               style={{ borderColor: 'rgba(212, 175, 55, 0.4)', background: 'rgba(212, 175, 55, 0.08)' }}>
            <Crown className="h-3.5 w-3.5" style={{ color: '#D4AF37' }} />
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: '#D4AF37' }}>
              Verified Power Partner — Annual
            </span>
          </div>

          {/* Scarcity badge — pulses when slots are low */}
          {availability && (() => {
            const remaining = availability.user_city
              ? availability.user_city.remaining
              : availability.country_remaining;
            const cap = availability.user_city
              ? availability.user_city.cap
              : availability.country_cap;
            const scope = availability.user_city
              ? availability.user_city.city
              : `${country === 'UK' ? 'United Kingdom' : country === 'CA' ? 'Canada' : 'India'}`;
            const isLow = remaining <= Math.max(2, Math.ceil(cap * 0.4));
            const isFull = remaining === 0;
            return (
              <div
                data-testid="power-scarcity-badge"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border ${isLow ? 'animate-pulse' : ''}`}
                style={{
                  borderColor: isFull ? 'rgba(244, 63, 94, 0.5)' : isLow ? 'rgba(244, 114, 22, 0.5)' : 'rgba(16, 185, 129, 0.5)',
                  background: isFull ? 'rgba(244, 63, 94, 0.12)' : isLow ? 'rgba(244, 114, 22, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                }}
              >
                <Flame className="h-4 w-4" style={{
                  color: isFull ? '#F43F5E' : isLow ? '#F47216' : '#10B981',
                }} />
                <span className="text-xs sm:text-sm font-semibold" style={{
                  color: isFull ? '#FCA5A5' : isLow ? '#FED7AA' : '#A7F3D0',
                }}>
                  {isFull
                    ? `${scope} is fully booked — join the waitlist below`
                    : `Only ${remaining} of ${cap} Power Partner slot${remaining === 1 ? '' : 's'} left for ${scope}`}
                </span>
              </div>
            );
          })()}
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl font-semibold leading-tight mb-4">
            Sync your <span className="italic" style={{ color: '#D4AF37' }}>entire portfolio</span><br />
            to high-intent NRI buyers.
          </h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl">
            Built for high-volume agents. One-click bulk upload, AI-matched warm leads,
            priority routing on every NRI search, and a Power Agent badge that signals
            trust to investors from the UK and Canada.
          </p>

          {/* Country toggle */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs tracking-widest uppercase text-white/50 font-bold mr-2">Region</span>
            {Object.entries(POWER_QUOTES).map(([cc, q]) => (
              <button
                key={cc}
                onClick={() => setCountry(cc)}
                data-testid={`power-country-${cc.toLowerCase()}`}
                className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase rounded transition-all ${
                  country === cc
                    ? 'bg-[#D4AF37] text-[#0A0A0C]'
                    : 'border border-white/20 text-white/70 hover:bg-white/5'
                }`}
              >
                {cc} · {q.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold">Annual fee</div>
              <div className="font-['Playfair_Display'] text-5xl sm:text-6xl font-semibold" style={{ color: '#D4AF37' }}>
                {quote.label}
              </div>
              <div className="text-xs text-white/50 mt-1">365 days · cancel anytime</div>
            </div>
            <Button
              onClick={startCheckout}
              disabled={checkoutLoading || loading}
              data-testid="power-upgrade-checkout-btn"
              className="ml-0 sm:ml-8 bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#E5C865] px-10 py-7 text-sm tracking-widest uppercase font-bold shadow-lg"
            >
              {checkoutLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout…</>
              ) : (
                <>Activate Power Partner <ArrowRight className="ml-3 h-4 w-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* 3× ROI Guarantee */}
      <div
        className="relative rounded-2xl p-8 border-2"
        style={{ borderColor: '#D4AF37', background: 'linear-gradient(135deg, #FEF3EC 0%, #FFFBF5 100%)' }}
        data-testid="power-3x-roi"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
               style={{ background: '#D4AF37' }}>
            <Award className="h-7 w-7 text-[#0A0A0C]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C4652E] font-bold mb-1">
              Win-Win Guarantee
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0A0A0C] mb-2">
              3× ROI or Year-2 is on us.
            </h3>
            <p className="text-[#1F2937]/80 text-sm leading-relaxed">
              If we don't route you at least <b>{quote.symbol}{(quote.amount * 3).toLocaleString()}</b> in
              verified leads within 12 months of activation, your <b>Year-2 renewal is FREE</b>. We win when you win.
            </p>
          </div>
        </div>
      </div>

      {/* Recoupment maths — the exact economics SAGE pitches */}
      <div className="rounded-2xl p-8 bg-[#F8FAFC] border border-slate-200" data-testid="power-recoup-math">
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="h-6 w-6 text-emerald-600" />
          <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0A0A0C]">
            How to make membership free
          </h3>
        </div>
        <p className="text-[#1F2937]/70 text-sm mb-6 max-w-2xl">
          Every paid agent gets a unique referral link. We pay you{' '}
          <b className="text-emerald-700">{quote.referralPayout} cash</b> per fellow agent who joins via your link.
          No cap. Stack the maths:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-300">
                <th className="text-left py-3 px-4 text-[10px] tracking-widest uppercase text-slate-500 font-bold">Referrals</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-widest uppercase text-slate-500 font-bold">You earn</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-widest uppercase text-slate-500 font-bold">Net</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-widest uppercase text-slate-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody className="font-medium">
              {[
                { n: 4,  status: '25% recovered',           emoji: null },
                { n: quote.recoupGoal, status: 'Near break-even',   emoji: null },
                { n: quote.breakeven, status: '100% paid + profit',   emoji: '✓' },
                { n: 25, status: 'Pure profit zone',        emoji: '★' },
                { n: 50, status: 'Year-2 covered + bonus',  emoji: '★★' },
              ].map((row) => {
                const earn = row.n * (POWER_QUOTES[country].referralPayout === '₹750' ? 750 :
                                       POWER_QUOTES[country].referralPayout === '£10' ? 10 : 15);
                const net = earn - quote.amount;
                const isPositive = net >= 0;
                return (
                  <tr key={row.n} className="border-b border-slate-200">
                    <td className="py-3 px-4 text-[#0A0A0C]">
                      <span className="font-semibold">{row.n}</span> agents
                    </td>
                    <td className="py-3 px-4 text-[#0A0A0C]">
                      {quote.symbol}{earn.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold ${isPositive ? 'text-emerald-700' : 'text-orange-700'}`}>
                      {isPositive ? '+' : ''}{quote.symbol}{net.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isPositive ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {row.emoji && <span>{row.emoji}</span>}
                        {row.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Year-2 renewal is just a {quote.symbol}{(quote.amount * 0.1).toFixed(0)} increment ({(quote.amount + (quote.amount * 0.1)).toLocaleString()} {quote.currency}).
          From referrals alone you stay net-positive every year.
        </p>
      </div>

      {/* What you get */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="power-features-grid">
        {[
          { icon: <Zap />,        title: 'Bulk Inventory Dashboard',     body: 'Drag-and-drop CSV upload. Our AI maps your portfolio to NRI-friendly fields and converts prices to GBP automatically.' },
          { icon: <Users />,      title: 'Priority NRI Buyer Routing',   body: 'Verified UK and Canada buyer matches land in your inbox before they hit the public digest.' },
          { icon: <Sparkles />,   title: 'AI-Matched Warm Leads',        body: 'Pulse engine cross-references buyer search history with your portfolio and surfaces only high-intent matches.' },
          { icon: <ShieldCheck />,title: 'Power Agent Badge',            body: 'Visible on your public profile and every listing — signals trust and unlocks 40% higher response rates.' },
          { icon: <Award />,      title: 'Pulse Investment Reports',     body: 'Unlimited custom city reports for your clients. Yield forecasts, capital appreciation, demand heatmaps.' },
          { icon: <Crown />,      title: 'CEO Hot-Lead Channel',         body: 'When you flag a corporate or franchise opportunity, it routes directly to the CEO\'s WhatsApp inside 5 minutes.' },
        ].map((feat, i) => (
          <div key={i} className="p-6 bg-white rounded-xl border border-slate-200 hover:border-[#D4AF37]/50 hover:shadow-md transition-all">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                 style={{ background: 'linear-gradient(135deg, #FEF3EC 0%, #FFE7D1 100%)' }}>
              {React.cloneElement(feat.icon, { className: 'h-5 w-5 text-[#C4652E]' })}
            </div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#0A0A0C] font-semibold mb-2">{feat.title}</h4>
            <p className="text-sm text-slate-600 leading-relaxed">{feat.body}</p>
          </div>
        ))}
      </div>

      {/* Comparison vs Standard */}
      <div className="rounded-2xl p-8 bg-white border border-slate-200" data-testid="power-vs-standard">
        <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0A0A0C] mb-6">
          Power Partner vs Standard — at a glance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 text-[10px] tracking-widest uppercase text-slate-500 font-bold w-1/2">Feature</th>
                <th className="text-center py-3 text-[10px] tracking-widest uppercase text-slate-500 font-bold">Standard ({country === 'IN' ? '₹2,499/mo' : country === 'UK' ? '£29/mo' : 'C$49/mo'})</th>
                <th className="text-center py-3 text-[10px] tracking-widest uppercase font-bold" style={{ color: '#C4652E' }}>Power ({quote.label}/yr)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Listings', 'Up to 10', 'Unlimited bulk upload'],
                ['Priority NRI routing', false, true],
                ['AI-matched warm leads', false, true],
                ['Power Agent badge', false, true],
                ['Pulse Reports', '£49 each', 'Unlimited'],
                ['Affiliate payout', `${quote.referralPayout}/ref`, `${quote.referralPayout}/ref`],
                ['CEO hot-lead channel', false, true],
                ['3× ROI guarantee', false, true],
              ].map(([label, std, pwr]) => (
                <tr key={label} className="border-b border-slate-100">
                  <td className="py-3 text-[#0A0A0C] font-medium">{label}</td>
                  <td className="py-3 text-center text-slate-600">
                    {std === true ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> :
                     std === false ? <span className="text-slate-300">—</span> : <span>{std}</span>}
                  </td>
                  <td className="py-3 text-center font-semibold" style={{ color: '#0A0A0C' }}>
                    {pwr === true ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> :
                     pwr === false ? <span className="text-slate-300">—</span> : <span>{pwr}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isStandard && (
          <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-3 mt-6">
            <CheckCircle2 className="h-4 w-4 inline mr-1.5" />
            You're currently on the Standard tier. Upgrading swaps to Power immediately and the annual window starts today.
          </p>
        )}
      </div>

      {/* Per-city availability grid — visible scarcity per priority city */}
      {availability && availability.cities?.length > 0 && (
        <div className="rounded-2xl p-8 bg-white border border-slate-200" data-testid="power-city-availability">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="h-5 w-5 text-[#C4652E]" />
            <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0A0A0C]">
              City availability — {country === 'UK' ? 'United Kingdom' : country === 'CA' ? 'Canada' : 'India'}
            </h3>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Capped at {availability.city_cap} Power Partners per city to protect your buyer-routing exclusivity.
            Slots open when an existing partner doesn't renew.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {availability.cities.map((c) => {
              const isFull = c.remaining === 0;
              const isLow = c.remaining > 0 && c.remaining <= 2;
              return (
                <div
                  key={c.city}
                  data-testid={`power-city-${c.city.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`rounded-lg border p-3 flex flex-col items-start gap-1 transition-colors ${
                    isFull ? 'border-rose-200 bg-rose-50/40' :
                    isLow ? 'border-orange-200 bg-orange-50/40' :
                    'border-emerald-200 bg-emerald-50/30'
                  }`}
                >
                  <span className="text-sm font-semibold text-[#0A0A0C]">{c.city}</span>
                  <span className={`text-xs font-bold ${
                    isFull ? 'text-rose-700' : isLow ? 'text-orange-700' : 'text-emerald-700'
                  }`}>
                    {isFull ? 'Fully booked' : `${c.remaining} of ${c.cap} left`}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-5">
            {availability.country_remaining} of {availability.country_cap} country-wide slots remaining.
            Upgrade now to lock your city.
          </p>
        </div>
      )}

      {/* Final CTA */}
      <div className="text-center pt-4">
        <Button
          onClick={startCheckout}
          disabled={checkoutLoading || loading}
          data-testid="power-upgrade-checkout-btn-bottom"
          className="bg-[#0A0A0C] text-white hover:bg-[#1F2937] px-12 py-7 text-sm tracking-widest uppercase font-bold shadow-lg"
        >
          {checkoutLoading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout…</>
          ) : (
            <>Activate Power Partner — {quote.label} <ArrowRight className="ml-3 h-4 w-4" /></>
          )}
        </Button>
        <p className="text-xs text-slate-500 mt-4">
          Secure Stripe checkout. Cancel anytime. 3× ROI guarantee in writing.
        </p>
      </div>
    </div>
  );
};

export default PowerPartnerUpgrade;
